/**
 * Incident Helper Functions
 * Utility functions for formatting and styling incident data
 */

import type { IncidentStatus, IncidentStatusReason } from '@/types/incidents';

export function getSeverityColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'bg-red-500 hover:bg-red-600 text-white';
    case 'high':
      return 'bg-orange-500 hover:bg-orange-600 text-white';
    case 'medium':
      return 'bg-yellow-500 hover:bg-yellow-600 text-white';
    case 'low':
      return 'bg-green-500 hover:bg-green-600 text-white';
    default:
      return 'bg-gray-500 hover:bg-gray-600 text-white';
  }
}

export function getSeverityLabel(severity: string): string {
  const labels: Record<string, string> = {
    critical: 'Crítico',
    high: 'Alto',
    medium: 'Medio',
    low: 'Bajo',
  };
  return labels[severity] || severity;
}

export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'urgent':
      return 'bg-red-500 hover:bg-red-600 text-white';
    case 'high':
      return 'bg-orange-500 hover:bg-orange-600 text-white';
    case 'medium':
      return 'bg-yellow-500 hover:bg-yellow-600 text-white';
    case 'low':
      return 'bg-blue-500 hover:bg-blue-600 text-white';
    default:
      return 'bg-gray-500 hover:bg-gray-600 text-white';
  }
}

/**
 * @deprecated Use getStatusReasonColor() instead. Legacy function kept for backward compatibility.
 */
export function getStateColor(state: string): string {
  switch (state) {
    case 'new':
      return 'bg-blue-500 text-white';
    case 'assigned':
      return 'bg-indigo-500 text-white';
    case 'investigation':
      return 'bg-purple-500 text-white';
    case 'containment':
      return 'bg-yellow-500 text-white';
    case 'remediation':
      return 'bg-orange-500 text-white';
    case 'post_incident':
      return 'bg-teal-500 text-white';
    case 'resolved':
      return 'bg-green-500 text-white';
    case 'closed':
      return 'bg-gray-500 text-white';
    case 'cancelled':
      return 'bg-red-500 text-white';
    case 'on_hold':
      return 'bg-orange-500 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
}

/**
 * @deprecated Use getStatusReasonLabel() instead. Legacy function kept for backward compatibility.
 */
export function getStateLabel(state: string): string {
  const labels: Record<string, string> = {
    new: 'Nuevo',
    assigned: 'Asignado',
    investigation: 'Investigación',
    containment: 'Contención',
    remediation: 'Remediación',
    post_incident: 'Post-Incidente',
    resolved: 'Resuelto',
    closed: 'Cerrado',
    cancelled: 'Cancelado',
    on_hold: 'En Espera',
  };
  return labels[state] || state;
}

/**
 * PROJ-036: Get Spanish label for incident status (high-level lifecycle status)
 */
export function getStatusLabel(status: IncidentStatus): string {
  const labels: Record<IncidentStatus, string> = {
    active: 'Activo',
    resolved: 'Resuelto',
    cancelled: 'Cancelado',
  };
  return labels[status];
}

/**
 * PROJ-036: Get color classes for incident status (high-level lifecycle status)
 */
export function getStatusColor(status: IncidentStatus): string {
  switch (status) {
    case 'active':
      return 'bg-blue-500 text-white';
    case 'resolved':
      return 'bg-green-500 text-white';
    case 'cancelled':
      return 'bg-red-500 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
}

/**
 * PROJ-036: Get Spanish label for incident status reason (granular status)
 */
export function getStatusReasonLabel(statusReason: IncidentStatusReason): string {
  const labels: Record<IncidentStatusReason, string> = {
    // Active status reasons
    new: 'Nuevo',
    in_progress: 'En Progreso',
    researching: 'Investigando',
    containment: 'Contención',
    remediation: 'Remediación',
    on_hold: 'En Espera',
    waiting_for_details: 'Esperando Detalles',
    post_incident: 'Post-Incidente',
    // Resolved status reasons
    problem_solved: 'Problema Resuelto',
    information_provided: 'Información Provista',
    // Cancelled status reasons
    cancelled: 'Cancelado',
    merged: 'Fusionado',
  };
  return labels[statusReason];
}

/**
 * PROJ-036: Get color classes for incident status reason (granular status)
 */
export function getStatusReasonColor(statusReason: IncidentStatusReason): string {
  switch (statusReason) {
    // Active status reasons - Blues, Purples, Yellows, Oranges
    case 'new':
      return 'bg-blue-500 text-white';
    case 'in_progress':
      return 'bg-indigo-500 text-white';
    case 'researching':
      return 'bg-purple-500 text-white';
    case 'containment':
      return 'bg-yellow-500 text-white';
    case 'remediation':
      return 'bg-orange-500 text-white';
    case 'on_hold':
      return 'bg-amber-500 text-white';
    case 'waiting_for_details':
      return 'bg-sky-500 text-white';
    case 'post_incident':
      return 'bg-teal-500 text-white';
    // Resolved status reasons - Greens
    case 'problem_solved':
      return 'bg-green-500 text-white';
    case 'information_provided':
      return 'bg-emerald-500 text-white';
    // Cancelled status reasons - Reds/Grays
    case 'cancelled':
      return 'bg-red-500 text-white';
    case 'merged':
      return 'bg-gray-500 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
}
